import styles from "./PaginationList.module.scss";
import { Button, Select } from "antd";
import { useCallback, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import classNames from "classnames";
import useDeviceCheck from "Hooks/useDeviceCheck";

export const TOTAL_PAGE_PER_FETCH = 5;

const InfinityPagination = ({
  currentMaxPage = 0,
  pagination,
  setPagination,
  sizeChanger = true,
  pageSizeOptions = [10, 20, 50, 100],
  hasNextPage = false,
  isLoading = false,
  numPagesPerFetch = TOTAL_PAGE_PER_FETCH,
  style,
  addLink = false,
  hasLink = false,
  getPageHref,
}) => {
  const location = useLocation();
  const { isMobile } = useDeviceCheck();
  const prevPageRef = useRef(pagination.page);
  const currentDisplayPage = useMemo(() => {
    if (isLoading) {
      return prevPageRef.current;
    } else {
      prevPageRef.current = pagination.page;
      return pagination.page;
    }
  }, [pagination.page, isLoading]);

  const onChange = (newPage, newPageSize) => {
    // setLoading(true);
    setPagination({
      ...pagination,
      page: newPage,
      rowsPerPage: newPageSize,
    });
  };

  const getHref = useCallback(
    (targetPage) => {
      if (!addLink && !hasLink && !getPageHref) return undefined;
      const validPage = Math.max(1, targetPage);
      if (typeof getPageHref === "function") {
        return getPageHref(validPage);
      }
      if (typeof addLink === "function") {
        return addLink(validPage);
      }
      if (location) {
        try {
          const searchParams = new URLSearchParams(location.search);
          if (validPage <= 1) {
            searchParams.delete("page");
          } else {
            searchParams.set("page", validPage);
          }
          const search = searchParams.toString();
          return `${location.pathname}${search ? `?${search}` : ""}`;
        } catch {
          return undefined;
        }
      }
      return undefined;
    },
    [addLink, hasLink, getPageHref, location]
  );

  const handleClick = (e, newPage) => {
    const href = getHref(newPage);
    if (href) {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) {
        return;
      }
      e.preventDefault();
    }
    onChange(newPage, pagination.rowsPerPage);
  };

  const isPrevDisabled = currentDisplayPage === 1;
  const isNextDisabled = !hasNextPage && currentDisplayPage === currentMaxPage;

  return (
    <div className={styles["pagination-list"]} style={style}>
      <>
        <Button
          className={styles["pagination-item"]}
          icon={<ChevronLeftIcon width={12} height={12} />}
          onClick={(e) => handleClick(e, currentDisplayPage - 1)}
          disabled={isPrevDisabled}
          href={!isPrevDisabled ? getHref(currentDisplayPage - 1) : undefined}
        />
        {isMobile ? (
          <div className={styles["pagination-item"]}>{currentDisplayPage}</div>
        ) : (
          <>
            {currentDisplayPage > 6 && (
              <div className={styles["pagination-item"]}>...</div>
            )}
            {[
              ...Array(
                currentDisplayPage > numPagesPerFetch
                  ? numPagesPerFetch
                  : currentDisplayPage - 1,
              ).keys(),
            ]
              .reverse()
              .map((e) => {
                const pageNum = currentDisplayPage - e - 1;
                return (
                  <Button
                    className={classNames(styles["pagination-item"], {
                      [styles["active"]]: pageNum === currentDisplayPage,
                    })}
                    key={pageNum}
                    href={getHref(pageNum)}
                    onClick={(e) => handleClick(e, pageNum)}
                  >
                    {pageNum}
                  </Button>
                );
              })}
            {Array.from({
              length: Math.min(
                currentMaxPage - currentDisplayPage + 1,
                numPagesPerFetch,
              ),
            }).map((_, index) => {
              const pageNum = currentDisplayPage + index;
              return (
                <Button
                  className={classNames(styles["pagination-item"], {
                    [styles["active"]]: pageNum === currentDisplayPage,
                  })}
                  key={pageNum}
                  href={getHref(pageNum)}
                  onClick={(e) => handleClick(e, pageNum)}
                >
                  {pageNum}
                </Button>
              );
            })}
            {(hasNextPage ||
              currentDisplayPage < currentMaxPage - numPagesPerFetch + 1) && (
              <div className={styles["pagination-item"]}>...</div>
            )}
          </>
        )}
        <Button
          className={styles["pagination-item"]}
          icon={<ChevronRightIcon width={12} height={12} />}
          onClick={(e) => handleClick(e, currentDisplayPage + 1)}
          disabled={isNextDisabled}
          href={!isNextDisabled ? getHref(currentDisplayPage + 1) : undefined}
        />
        {sizeChanger && (
          <Select
            style={{ width: 120 }}
            value={pagination.rowsPerPage}
            onChange={(newPageSize) =>
              onChange(currentDisplayPage, newPageSize)
            }
            options={pageSizeOptions.map((size) => ({
              value: size,
              label: `${size} / page`,
            }))}
          />
        )}
      </>
    </div>
  );
};

export default InfinityPagination;
